/**
 * @fileoverview AR-670 — lazy fetch of the school's local Classroom course
 * catalog, used by the "link an existing course" select. The catalog answers
 * 200 even in needs_reconnection (cached data + connectionStatus flag); a 422
 * only happens with an empty catalog, and `failedCode` tells the modal apart
 * "never connected" from a real API failure.
 */

import { useCallback, useState } from 'react'

import { getClassroomAdapters } from '../provider'
import {
  ClassroomCourseOption,
  ClassroomErrorCode,
  isClassroomPortError,
  SchoolConnectionStatus,
} from '../types'

export interface ClassroomCoursesState {
  courses: ClassroomCourseOption[]
  loading: boolean
  failed: boolean
  /** Normalized code of the last failure (null while healthy). */
  failedCode: ClassroomErrorCode | null
  syncedAt: string | null
  connectionStatus: SchoolConnectionStatus | null
  load: () => Promise<void>
}

export function useClassroomCourses(): ClassroomCoursesState {
  const { courses: coursesPort } = getClassroomAdapters()
  const [courses, setCourses] = useState<ClassroomCourseOption[]>([])
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const [failedCode, setFailedCode] = useState<ClassroomErrorCode | null>(null)
  const [syncedAt, setSyncedAt] = useState<string | null>(null)
  const [connectionStatus, setConnectionStatus] = useState<SchoolConnectionStatus | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setFailed(false)
    setFailedCode(null)
    try {
      const catalog = await coursesPort.listCourses()
      setCourses(catalog.courses)
      setSyncedAt(catalog.syncedAt)
      setConnectionStatus(catalog.connectionStatus)
    } catch (error) {
      setFailed(true)
      setFailedCode(isClassroomPortError(error) ? error.code : 'classroom_api_error')
    } finally {
      setLoading(false)
    }
  }, [coursesPort])

  return { courses, loading, failed, failedCode, syncedAt, connectionStatus, load }
}
